import dayjs from "dayjs";
import {getPercentages} from "../Colors/percentages";
import Color from 'color';

/**
 * Calculates the percentage of the score based on the given range.
 * Ensures the result is clamped between 15% and 100% for display purposes.
 *
 * @param {number} lowerRange - The lower bound of the range.
 * @param {number} higherRange - The upper bound of the range.
 * @param {number} score - The current score to be evaluated.
 * @returns {number} The calculated percentage.
 */
export const calculatePercentage = (lowerRange, higherRange, score) => {
  if (
    [lowerRange, higherRange, score].some(
      (item) => item === undefined || item === null
    )
  ) {
    return 0;
  }
  const val = (score - lowerRange) / (higherRange - lowerRange);
  const result = Math.max(15, val * 100); // Ensure minimum of 15%
  return Math.min(result, 100); // Ensure maximum of 100%
};

/**
 * Determines the range associated with a given color.
 *
 * @param {string} color - The color to determine the range for.
 * @returns {Array<number>} A two-element array with the lower and upper bounds of the range.
 */
export const determineRange = (color) => {
  if (!color) return [0, 0];

  switch (color.toLowerCase()) {
    case "green":
      return [0, 29];
    case "yellow":
      return [30, 55];
    case "red":
      return [56, 100];
    default:
      return [0, 0];
  }
};

/**
 * Rounds a number to one decimal place, adding precision without too much complexity.
 *
 * @param {number} num - The number to round.
 * @returns {number} The rounded number.
 */
export const round = (num) => {
  return Math.round((num + Number.EPSILON) * 10) / 10;
};

/**
 * Generates a string representation of a date in the format "YYYY-MM-DD".
 *
 * @param {Date} date - The date to format.
 * @returns {string} The formatted date string.
 */
export const getDateString = (date) => {
  return dayjs(date).format("YYYY-MM-DD");
};

/**
 * Determines the style properties based on the information associated with a calendar date.
 * This includes the color for display, font color, background color, and shade color,
 * all determined based on the color coding and score in the `info` object.
 *
 * @param {object} info - The information object containing details about the calendar date, including color and score.
 * @returns {object} An object containing style properties for display, font, background, and shade colors.
 */
export const getColorStyles = (info, difficultyBarConfig) => {
  let borderColor, fontColor, backgroundColor, shadeColor, background;

  const { color } = info;

  let fillPercent = calculatePercentage(...determineRange(color), info.score);

  if (info["opponent"] != null) {
    // Game day logic
    fontColor = "white";
    switch (color) {
      case "red":
        borderColor = "#d9534f";
        backgroundColor = "#d9534f";
        shadeColor = "#d9534f66";
        break;
      case "high_yellow":
        borderColor = "#cc6600";
        backgroundColor = "#cc6600";
        shadeColor = "#cc660066";
        break;
      case "yellow":
        borderColor = "#FFC83D";
        backgroundColor = "#FFC83D";
        shadeColor = "#FFC83D66";
        break;
      case "green":
        borderColor = "#31B452";
        backgroundColor = "#31B452";
        shadeColor = "#31B45266";
        break;
      case "blue":
        borderColor = "#0096FF";
        backgroundColor = "#FFFFFF";
        shadeColor = "#FFFFFF";
        break;
      default:
        borderColor = "#0096FF"; // Fallback color
        backgroundColor = "#FFFFFF"; // Fallback background color
        shadeColor = "#FFFFFF"; // Fallback shade color
    }
  } else {
    switch (color) {
      case "red":
        borderColor = "#d9534f";
        fontColor = "#d9534f";
        backgroundColor = "#FFE0E0";
        shadeColor = "#d9534f66";
        break;
      case "high_yellow":
        borderColor = "#cc6600";
        fontColor = "#cc6600";
        backgroundColor = "#FFF9D2";
        shadeColor = "#cc660066";
        break;
      case "yellow":
        borderColor = "#FFC83D";
        fontColor = "#FFC83D";
        backgroundColor = "#FFF9E0";
        shadeColor = "#FFC83D66";
        break;
      case "green":
        borderColor = "#31B452";
        fontColor = "#31B452";
        backgroundColor = "#E0FFE3";
        shadeColor = "#31B45266";
        break;
      case "blue":
        borderColor = "#0096FF";
        fontColor = "#0096FF";
        backgroundColor = "#FFFFFF";
        shadeColor = "#FFFFFF";
        break;
      case "lime green":
        borderColor = "#94ff8a";
        fontColor = "#94ff8a";
        backgroundColor = "#FFFFFF";
        shadeColor = "#FFFFFF";
        break;
      case "low orange":
        borderColor = "#ffce8a";
        fontColor = "#ffce8a";
        backgroundColor = "#FFFFFF";
        shadeColor = "#FFFFFF";
        break;
      case "hot red":
        borderColor = "#FF51E2";
        fontColor = "#FF51E2";
        backgroundColor = "#FFFFFF";
        shadeColor = "#FFFFFF";
        break;
      default:
        borderColor = "#0096FF";
        fontColor = "#000";
        backgroundColor = "#FFFFFF";
        shadeColor = "#FFFFFF";
    }
  }
  const { score } = info;
  
  if (score && difficultyBarConfig && difficultyBarConfig.colors && difficultyBarConfig.thresholds) {
    const { colors, thresholds } = difficultyBarConfig;
    const normalizedScore = score / 100;

    let segmentIndex = 0;
    for (let i = 0; i < thresholds.length; i++) {
      if (normalizedScore <= thresholds[i]) {
        segmentIndex = i;
        break;
      } else {
        segmentIndex = i + 1;
      }
    }
    
    const segmentStart = segmentIndex === 0 ? 0 : thresholds[segmentIndex - 1];
    const segmentEnd = thresholds[segmentIndex] || 1;
    
    if (segmentEnd > segmentStart) {
      fillPercent = Math.max(15, Math.min(100, ((normalizedScore - segmentStart) / (segmentEnd - segmentStart)) * 100));
    } else if (segmentIndex === thresholds.length) {
      // The last segment ends at 1.0, so a last threshold at or above 100% leaves it
      // no width. Every score in it is a full cell rather than the named-colour fill.
      fillPercent = 100;
    }

    const segmentColor = colors[segmentIndex];
    
    if (segmentColor) {
      borderColor = segmentColor;
      const baseColor = Color(segmentColor);
      backgroundColor = baseColor.alpha(0.2).string();
      shadeColor = baseColor.alpha(0.5).string();
      fontColor = baseColor.darken(0.3).string();
    }
  }

  if (info["score"]) {
    background = `linear-gradient(${backgroundColor} ${
      100 - fillPercent
    }%, ${shadeColor} ${100 - fillPercent}%, ${shadeColor} 100%)`;
  }

  if (info["opponent"]) {
    fontColor = "#000";
    borderColor = "#000";
    if (!info["score"]) {
      backgroundColor = "#f09892";
    }
  } else if (info["is_prediction"] && !info["total_time"]) {
    backgroundColor = "white";
    background = "white";
  }

  return {
    borderColor,
    fontColor,
    backgroundColor,
    fillPercent,
    background,
  };
};

/** Base hex the /calendar/month endpoint resolves for the "blue" (game/no-data) colour. */
export const CALENDAR_BLUE_HEX = "#0096FF";

// Non-game tints from getColorStyles, re-keyed by the base hex the server now
// resolves. The white-backgrounded entries are deliberate: those days carry no
// intensity to shade, so only the border and font take the colour. high_yellow is
// kept for fidelity with getColorStyles even though nothing assigns it today.
const CELL_TINTS = {
  "#d9534f": { font: "#d9534f", background: "#FFE0E0", shade: "#d9534f66" }, // red
  "#cc6600": { font: "#cc6600", background: "#FFF9D2", shade: "#cc660066" }, // high_yellow
  "#FFC83D": { font: "#FFC83D", background: "#FFF9E0", shade: "#FFC83D66" }, // yellow
  "#31B452": { font: "#31B452", background: "#E0FFE3", shade: "#31B45266" }, // green
  [CALENDAR_BLUE_HEX]: { font: CALENDAR_BLUE_HEX, background: "#FFFFFF", shade: "#FFFFFF" },
  "#94ff8a": { font: "#94ff8a", background: "#FFFFFF", shade: "#FFFFFF" }, // lime green
  "#ffce8a": { font: "#ffce8a", background: "#FFFFFF", shade: "#FFFFFF" }, // low orange
  "#FF51E2": { font: "#FF51E2", background: "#FFFFFF", shade: "#FFFFFF" }, // hot red
};

// Game-day tints: solid backgrounds. Font and border are forced black below.
// The three future-indicator colours have no case in getColorStyles' game-day
// switch, so they take its white default here rather than a tint of their hex.
const GAME_CELL_TINTS = {
  "#d9534f": { background: "#d9534f", shade: "#d9534f66" },
  "#cc6600": { background: "#cc6600", shade: "#cc660066" },
  "#FFC83D": { background: "#FFC83D", shade: "#FFC83D66" },
  "#31B452": { background: "#31B452", shade: "#31B45266" },
  [CALENDAR_BLUE_HEX]: { background: "#FFFFFF", shade: "#FFFFFF" },
  "#94ff8a": { background: "#FFFFFF", shade: "#FFFFFF" },
  "#ffce8a": { background: "#FFFFFF", shade: "#FFFFFF" },
  "#FF51E2": { background: "#FFFFFF", shade: "#FFFFFF" },
};

const hasDifficultyBarColors = (difficultyBarConfig) =>
  Boolean(difficultyBarConfig?.colors?.length && difficultyBarConfig?.thresholds?.length);

/**
 * getColorStyles' counterpart for the /calendar/month cell contract, where the
 * server already resolved the base hex (`color`) and gradient height (`fill`)
 * instead of sending a colour *name*. Only the tints derived from that base are
 * left to do here, so the band thresholds stay in one place — the server.
 *
 * A difficulty-bar segment hex can collide with the fixed palette above, so the
 * team config — not the hex — decides which tint path applies, exactly as
 * getColorStyles' ordering does.
 *
 * @param {object} cell - A /calendar/month cell: { color, fill, score, is_game, is_prediction, has_training }.
 * @param {object} [difficultyBarConfig] - The team's difficulty bar config.
 * @returns {object} { borderColor, fontColor, backgroundColor, background }.
 */
export const getColorStylesFromHex = (cell, difficultyBarConfig) => {
  const { color, fill, score, is_game: isGame, is_prediction: isPrediction, has_training: hasTraining } = cell;
  const baseHex = color || CALENDAR_BLUE_HEX;

  let borderColor = baseHex;
  let fontColor;
  let backgroundColor;
  let shadeColor;

  const tint = score && hasDifficultyBarColors(difficultyBarConfig)
    ? undefined
    : (isGame ? GAME_CELL_TINTS : CELL_TINTS)[baseHex];

  if (tint) {
    backgroundColor = tint.background;
    shadeColor = tint.shade;
    fontColor = isGame ? "white" : tint.font;
  } else {
    const baseColor = Color(baseHex);
    fontColor = baseColor.darken(0.3).string();
    backgroundColor = baseColor.alpha(0.2).string();
    shadeColor = baseColor.alpha(0.5).string();
  }

  // The server nulls `fill` for the cases getColorStyles renders solid, so a
  // scored day with no fill is an unfilled prediction — overridden below.
  let background;
  if (score && fill != null) {
    const gradientStop = 100 - fill;
    background = `linear-gradient(${backgroundColor} ${gradientStop}%, ${shadeColor} ${gradientStop}%, ${shadeColor} 100%)`;
  }

  if (isGame) {
    fontColor = "#000";
    borderColor = "#000";
    if (!score) {
      backgroundColor = "#f09892";
    }
  } else if (isPrediction && !hasTraining) {
    backgroundColor = "white";
    background = "white";
  }

  return { borderColor, fontColor, backgroundColor, background };
};

export const RPE_METRICS = ["RPE", "session_RPE"];
const MAX_METRICS = ["top_speed", "accel_max", "decel_max", "top_speed_percent", "max_hr"];

const canAverageMetric = (key) => {
  return !(
    ["mean_hr", "trimp_per_payload", "average_speed", "physio_intensity", "skating_intensity"].includes(key) ||
    key.includes("per_min") || key.includes("per_minute") || key.includes("avg") || key.includes("_perc")
  );
};

export const getPredictionInfo = ({
  costPerMin,
  metrics,
  drills,
  dateString,
  data,
  practicePlan,
}) => {
  return metrics
    .filter(({ key }) => !RPE_METRICS.includes(key))
    .reduce(
      (cur, { key }) => ({
        ...cur,
        [key]: MAX_METRICS.includes(key)
          ? Math.max(
              ...costPerMin
                .filter((c) =>
                  practicePlan.map((d) => d.drill_id).includes(c.drill_id)
                )
                .map((o) => o?.[key] || 0)
            )
          : practicePlan.reduce(
              (cost, drill) =>
                cost +
                (canAverageMetric(key) ? drill.duration : 1) *
                  costPerMin.find((c) => c.drill_id === drill.drill_id)?.[key],
              0
            ) / (canAverageMetric(key) ? 1 : practicePlan.length || 1),
      }),
      {
        ...data[dateString],
        score: data[dateString]?.score,
        drills: practicePlan.map((drill) => ({
          name: drills.find((item) => item.drill_id === drill.drill_id).name,
          id: drill.drill_id,
          duration: drill.duration,
        })),
        prediction: true,
        date: dateString,
        is_prediction: true,
      }
    );
};

export const getMetricColor = (metric, value, colors) => {
  const { key } = metric;
  if (key === "distance") {
    value = round(value);
  } else {
    value = Math.round(value);
  }

  if (value > colors[key] * getPercentages(key).upper) {
    return "#d9534f";
  } else if (value > colors[key] * getPercentages(key).middle) {
    return "#EF7616";
  } else if (value > colors[key] * getPercentages(key).lower) {
    return "#FFC83D";
  } else if (value < colors[key] * getPercentages(key).lower) {
    return "#31B452";
  } else {
    return "black";
  }
};
